
"use client";

import { useState } from "react";
import { addReview } from "@/app/actions";
import { User, Lock } from "lucide-react";
import Link from "next/link";
import "./store/store-v5.css";
import { useSession } from "next-auth/react";

export default function StoreReviews({ storeId, storeName, initialRating, totalReviews }: { storeId: string, storeName: string, initialRating: number, totalReviews: number }) {
    // Client-side session: the page HTML is cached and identical for everyone.
    const { data: session } = useSession();
    const [userRating, setUserRating] = useState(0);
    const [comment, setComment] = useState("");
    const [submitted, setSubmitted] = useState(false);
    const [open, setOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleVote = async () => {
        if (!userRating) return;
        setIsSubmitting(true);
        await addReview(storeId, userRating, comment);
        setSubmitted(true);
        setIsSubmitting(false);
    };

    // No ratings yet: never show a score. A real average only exists once someone has rated.
    const hasRatings = totalReviews + (submitted ? 1 : 0) > 0;
    const displayRating = userRating || (hasRatings ? initialRating : 0);

    // No ratings yet (about 99.5% of store pages): no "Customer Reviews" heading, no empty score, no empty stars (SEO audit
    // 2026-10-24, owner-approved). One small button keeps the way to the first rating open; it shows the form when clicked.
    if (!hasRatings && !open) {
        return (
            <div className="sp-reviews sp-scope mb-8">
                <button type="button" onClick={() => setOpen(true)} className="text-sm text-gray-400 underline underline-offset-4 hover:text-white">
                    Rate this store
                </button>
            </div>
        );
    }

    return (
        <div className="sp-reviews sp-scope mb-8">
            <h2 className="text-xl font-bold text-white mb-4">Customer Reviews</h2>

            <div className="flex flex-col md:flex-row md:items-start gap-8">
                {/* Rating Stats */}
                <div className="flex items-center gap-4">
                    {hasRatings || userRating > 0 ? (
                        <div className="text-4xl font-bold text-white bg-white/5 py-4 px-6 rounded-xl border border-white/10">
                            {displayRating.toFixed(1)}
                        </div>
                    ) : (
                        <div className="sp-reviews__empty-score">No ratings yet</div>
                    )}
                    <div>
                        <div className="flex gap-1 text-yellow-400 text-xl mb-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                                <button
                                    key={star}
                                    type="button"
                                    aria-label={`Rate ${star} out of 5`}
                                    onClick={() => !submitted && session && setUserRating(star)}
                                    disabled={submitted || !session}
                                    className={`transition-transform hover:scale-110 ${star <= (userRating || Math.round(displayRating)) ? "opacity-100" : "opacity-30"} ${!session ? "cursor-not-allowed opacity-20" : ""}`}
                                >
                                    ★
                                </button>
                            ))}
                        </div>
                        <div className="text-sm text-gray-400">
                            {hasRatings ? `Based on ${totalReviews + (submitted ? 1 : 0)} verified ratings` : "Be the first to rate this store"}
                        </div>
                    </div>
                </div>

                {/* Review Form */}
                <div className="flex-1 w-full">
                    {session ? (
                        <>
                            {!submitted ? (
                                <div className="space-y-3 animate-in fade-in">
                                    <div className="flex items-center gap-2 mb-2">
                                        <div className="w-6 h-6 rounded-full bg-purple-600 flex items-center justify-center text-xs font-bold text-white">
                                            {session.user?.name?.charAt(0) || "U"}
                                        </div>
                                        <span className="text-sm text-gray-300 font-medium">{session.user?.name || "Verified User"}</span>
                                    </div>

                                    <textarea
                                        aria-label={`Your review of ${storeName}`}
                                        value={comment}
                                        onChange={(e) => setComment(e.target.value)}
                                        placeholder={`Share your experience with ${storeName} coupons...`}
                                        className="w-full bg-black/40 border border-white/10 rounded-xl p-4 text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:border-[#00ff9d]/50 transition-colors resize-none h-24"
                                    />

                                    <div className="flex justify-end gap-3 items-center">
                                        {userRating === 0 && <span className="text-xs text-yellow-400 font-bold">Select star rating to submit</span>}
                                        <button
                                            onClick={handleVote}
                                            disabled={!userRating || isSubmitting}
                                            className={`px-6 py-2 rounded-lg text-sm font-bold transition-all ${userRating && !isSubmitting ? "bg-[#00ff9d] text-black hover:bg-[#00cc7d]" : "bg-white/10 text-gray-400 cursor-not-allowed"}`}
                                        >
                                            {isSubmitting ? "Submitting..." : "Submit Review"}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="bg-[#00ff9d]/10 border border-[#00ff9d]/20 p-4 rounded-xl flex items-center gap-3 animate-in zoom-in-95">
                                    <div className="bg-[#00ff9d] text-black p-1 rounded-full">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg>
                                    </div>
                                    <div>
                                        <p className="text-[#00ff9d] text-sm font-bold">Review Submitted!</p>
                                        <p className="text-gray-400 text-xs text-wrap break-all max-w-[200px] md:max-w-none">Thanks for helping the community verify {storeName} codes.</p>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="bg-white/5 border border-white/10 p-4 rounded-xl flex flex-col items-center text-center">
                            <Lock className="w-8 h-8 text-gray-400 mb-2" />
                            <p className="text-gray-300 font-bold text-sm mb-1">Login to Rate & Review</p>
                            <p className="text-xs text-gray-400 mb-3 max-w-xs">Join our community to verify coupons, earn coins, and help others save.</p>
                            <Link href="/login" className="px-6 py-2 bg-purple-600 text-white text-xs font-bold rounded-lg hover:bg-purple-500 transition-colors">
                                Sign In / Register
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </div >
    );
}
